/**
 * MicListenModal.js
 * ==================
 * Bottom-sheet modal that lets the caregiver record a message and generate
 * AI response options without leaving the Quick Chat screen.
 *
 * Flow:
 *   1. Modal opens → speech recognition auto-starts
 *   2. Caregiver speaks (or types) → transcript appears
 *   3. Confirm ✓ → LLM generates 3 responses → navigate to /conversation/respond
 *   4. Cancel ⊖ → clear transcript
 *   5. Tap outside or ✕ → close modal
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateAiOptions } from './aiApi';
import './MicListenModal.css';

const BAR_HEIGHTS = [17, 24, 13, 35, 42, 26, 96, 66, 32, 31, 67, 59, 21, 13, 22, 31, 17, 10];

export default function MicListenModal({ onClose }) {
  const navigate = useNavigate();

  const [isListening,  setIsListening]  = useState(false);
  const [transcript,   setTranscript]   = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error,        setError]        = useState('');

  const recognitionRef     = useRef(null);
  const isListeningRef     = useRef(false);
  const finalTranscriptRef = useRef('');

  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  const speechSupported   = Boolean(SpeechRecognition);

  // ── Setup & auto-start speech recognition ──────────────────────────────────
  useEffect(() => {
    if (!speechSupported) return;

    const recognition = new SpeechRecognition();
    recognition.continuous     = true;
    recognition.interimResults = true;
    recognition.lang           = 'en-US';

    recognition.onresult = (event) => {
      let newFinal = '';
      let interim  = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const chunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) newFinal += chunk + ' ';
        else interim = chunk;
      }
      if (newFinal) {
        finalTranscriptRef.current = (finalTranscriptRef.current + newFinal).trimStart();
      }
      setTranscript((finalTranscriptRef.current + interim).trimStart());
    };

    recognition.onerror = (event) => {
      if (event.error === 'no-speech') return;
      setIsListening(false);
      isListeningRef.current = false;
    };

    recognition.onend = () => {
      if (isListeningRef.current) {
        try { recognition.start(); } catch (_) {}
      }
    };

    recognitionRef.current = recognition;

    // Auto-start after a brief delay to let the sheet animate in
    const t = setTimeout(() => {
      finalTranscriptRef.current = '';
      setTranscript('');
      setIsListening(true);
      isListeningRef.current = true;
      try { recognition.start(); } catch (_) {}
    }, 200);

    return () => {
      clearTimeout(t);
      recognition.onend = null;
      try { recognition.stop(); } catch (_) {}
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Controls ───────────────────────────────────────────────────────────────

  const startListening = useCallback(() => {
    if (!speechSupported || isListening) return;
    finalTranscriptRef.current = '';
    setTranscript('');
    setError('');
    setIsListening(true);
    isListeningRef.current = true;
    try { recognitionRef.current?.start(); } catch (_) {}
  }, [speechSupported, isListening]);

  const stopListening = useCallback(() => {
    setIsListening(false);
    isListeningRef.current = false;
    recognitionRef.current?.stop();
  }, []);

  const handleCancel = useCallback(() => {
    stopListening();
    finalTranscriptRef.current = '';
    setTranscript('');
    setError('');
  }, [stopListening]);

  const handleConfirm = useCallback(async () => {
    const text = transcript.trim();
    if (!text) { setError('Please record or type a message first.'); return; }
    stopListening();
    setIsGenerating(true);
    setError('');
    try {
      const responses = await generateAiOptions({ caregiverText: text });
      onClose();
      navigate('/conversation/respond', { state: { responses, caregiverText: text } });
    } catch (err) {
      setError(err.message || 'Failed to generate responses. Please try again.');
      setIsGenerating(false);
    }
  }, [transcript, stopListening, navigate, onClose]);

  const hasTranscript = transcript.trim().length > 0;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="mlm-overlay" onClick={onClose}>
      <div className="mlm-sheet" onClick={e => e.stopPropagation()}>

        {/* Handle + close */}
        <div className="mlm-header">
          <div className="mlm-handle" aria-hidden="true" />
          <button className="mlm-close-btn" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Start / stop pill */}
        <div className="mlm-top">
          <button
            className={`mlm-start-btn${isListening ? ' mlm-start-btn--active' : ''}`}
            onClick={isListening ? stopListening : startListening}
            disabled={isGenerating}
          >
            {isGenerating
              ? 'Generating…'
              : isListening
                ? 'Listening — tap to stop'
                : 'Start Recording'}
          </button>
        </div>

        {/* Sound bar visualiser */}
        <div className="mlm-soundbar" aria-hidden="true">
          {BAR_HEIGHTS.map((h, i) => (
            <div
              key={i}
              className={`mlm-bar${isListening ? ' mlm-bar--active' : ''}`}
              style={{ '--bar-height': `${h * 0.6}px`, '--bar-delay': `${(i * 40) % 600}ms` }}
            />
          ))}
        </div>

        {/* Status */}
        <p className="mlm-status">
          {isGenerating
            ? 'Generating responses…'
            : isListening
              ? 'Listening…'
              : hasTranscript
                ? 'Review your message'
                : speechSupported
                  ? 'Tap to start recording'
                  : 'Microphone not supported — type below'}
        </p>

        {/* Stop button */}
        {isListening && (
          <button className="mlm-stop-btn" onClick={stopListening} aria-label="Stop recording">
            <span className="mlm-stop-square" aria-hidden="true" />
          </button>
        )}

        {/* Transcript */}
        <div className="mlm-transcript-wrap">
          <textarea
            className="mlm-transcript"
            value={transcript}
            onChange={e => setTranscript(e.target.value)}
            placeholder="Your words will appear here — you can edit before sending."
            disabled={isGenerating}
            rows={4}
            aria-label="Caregiver message"
          />
          <div className="mlm-transcript-fade" aria-hidden="true" />
        </div>

        {error && <p className="mlm-error" role="alert">{error}</p>}

        {/* Actions */}
        <div className="mlm-actions">
          <button
            className="mlm-action-btn mlm-action-btn--cancel"
            onClick={handleCancel}
            disabled={isGenerating}
            aria-label="Cancel — discard and re-record"
          >
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <circle cx="12" cy="12" r="9" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
          </button>
          <button
            className="mlm-action-btn mlm-action-btn--confirm"
            onClick={handleConfirm}
            disabled={isGenerating || !hasTranscript}
            aria-label="Confirm — generate AI responses"
          >
            {isGenerating
              ? <span className="mlm-spinner" aria-hidden="true" />
              : (
                <svg viewBox="0 0 24 24" width="28" height="28" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                  strokeLinejoin="round">
                  <polyline points="4,12 10,18 20,6" />
                </svg>
              )}
          </button>
        </div>

      </div>
    </div>
  );
}
